import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Code, Bot, Rocket, Trophy, Calendar, GraduationCap, Monitor, Wrench, ChevronDown, Menu, X, Mail, Phone, MapPin, ArrowRight, Star, Zap, Globe, Clock, Home, ShoppingBag } from 'lucide-react';
import { Service } from '../types/index';
import { apiService, PublicStats } from '../services/api';
import { useSiteSettings } from '../hooks/useSiteSettings';

const PhoelynxPlatform = () => {
  const { contactEmail, homeHeroTitleLine1, homeHeroTitleLine2, homeHeroSubtitle } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [currentYear] = useState(new Date().getFullYear());
  const [, setActiveSection] = useState('home');
  const [publicStats, setPublicStats] = useState<PublicStats | null>(null);
  const [testimonials, setTestimonials] = useState<any[]>([]);

  // Formulaire de contact
  const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('sending');
    setContactError('');
    const res = await apiService.sendContact(contactForm);
    if (res.success) {
      setContactStatus('sent');
      setContactForm({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
    } else {
      setContactStatus('error');
      setContactError(res.error || 'Une erreur est survenue. Réessayez plus tard.');
    }
  };

  // Inscription newsletter (pied de page)
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('sending');
    const res = await apiService.subscribeNewsletter(newsletterEmail);
    if (res.success) {
      setNewsletterStatus('sent');
      setNewsletterMessage('Inscription confirmée, merci !');
      setNewsletterEmail('');
    } else {
      setNewsletterStatus('error');
      // « déjà inscrit » est renvoyé en 400 : message informatif, pas alarmant.
      setNewsletterMessage(/already/i.test(res.error || '') ? 'Cette adresse est déjà inscrite.' : 'Inscription impossible pour le moment.');
    }
  };

  // Tant que les compteurs ne sont pas chargés, la section n'est pas rendue :
  // mieux vaut ne rien afficher qu'afficher des zéros transitoires.
  useEffect(() => {
    apiService.getPublicStats()
      .then((r) => {
        if (r.success && r.data) setPublicStats(r.data);
      })
      .catch(() => setPublicStats(null));

    // Témoignages : la section reste masquée s'il n'y en a aucun de publié.
    apiService.getTestimonials()
      .then((r) => {
        if (r.success && r.data) setTestimonials(r.data);
      })
      .catch(() => setTestimonials([]));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = ['home', 'services', 'competitions', 'contact'];
    const observers = sections.map(sectionId => {
      const element = document.getElementById(sectionId);
      if (!element) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(sectionId);
          }
        },
        {
          threshold: 0.2,
          rootMargin: '-80px 0px -40% 0px'
        }
      );

      observer.observe(element);
      return { observer, element };
    });

    return () => {
      observers.forEach(obs => {
        if (obs) obs.observer.disconnect();
      });
    };
  }, []);

  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [formations, setFormations] = useState<any[]>([]);
  const [loadingFormations, setLoadingFormations] = useState(true);
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: any } = { Bot, Code, Monitor, Wrench, Trophy };
    const IconComponent = icons[iconName] || Bot;
    return <IconComponent className="w-8 h-8" />;
  };

  useEffect(() => {
    apiService.getServices()
      .then(response => {
        if (response.success && response.data) {
          const formattedServices = response.data.map((service: any) => ({
            ...service,
            icon: getIcon(service.icon),
            color: service.color
          }));
          setServices(formattedServices);
        }
        setLoadingServices(false);
      })
      .catch(() => {
        setLoadingServices(false);
      });
  }, []);

  useEffect(() => {
    apiService.getEventsByType('FORMATION')
      .then(response => {
        if (response.success && response.data) {
          const data = response.data;
          const now = new Date();

          // Filtrer toutes les formations futures marquées comme "featured"
          const upcomingFormations = data
            .filter((event: any) => {
              const eventDate = new Date(event.dateStart);
              // Formations futures ET marquées comme "à la une" (featured)
              return eventDate > now && event.featured === true;
            })
            .map((event: any) => {
              const date = new Date(event.dateStart);
              return {
                id: event.id,
              date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }).toUpperCase(),
              title: event.title,
              level: event.subtitle || 'Tous niveaux',
              duration: event.dateEnd ?
                (() => {
                  const startDate = new Date(event.dateStart);
                  const endDate = new Date(event.dateEnd);
                  const diffTime = endDate.getTime() - startDate.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                  return diffDays + (diffDays > 1 ? ' jours' : ' jour');
                })() :
                '1 jour',
              price: event.price || 'Sur devis',
              spots: event.maxParticipants - event.registeredParticipants,
              maxSpots: event.maxParticipants,
              description: event.description,
              topics: event.technologies || [],
              dateStart: event.dateStart,
              daysDifference: (new Date(event.dateStart).getTime() - now.getTime()) / (1000 * 3600 * 24)
            };
          })
          .sort((a: any, b: any) => a.daysDifference - b.daysDifference); // Trier par proximité

          setFormations(upcomingFormations);
        }
        setLoadingFormations(false);
      })
      .catch(() => {
        setLoadingFormations(false);
      });
  }, []);

  useEffect(() => {
    apiService.getEventsByType('COMPETITION')
      .then(response => {
        if (response.success && response.data) {
          const data = response.data;
          // Transformer les événements en format compétition
          const formattedCompetitions = data.map((event: any) => {
            const date = new Date(event.dateStart);

            return {
              id: event.id,
              date: date.getFullYear() > new Date().getFullYear() ?
                date.getFullYear().toString() :
                date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }).toUpperCase() + ' ' + date.getFullYear(),
              event: event.title,
              location: event.location,
              status: event.status === 'A_VENIR' ? 'À VENIR' :
                     event.status === 'EN_COURS' ? 'EN COURS' :
                     event.status === 'TERMINE' ? 'TERMINÉ' : event.status,
              description: event.description,
              maxParticipants: event.maxParticipants,
              registeredParticipants: event.registeredParticipants,
              price: event.price
            };
          }).sort((a: any, b: any) => {
            // Trier par statut (À VENIR en premier) puis par date
            if (a.status === 'À VENIR' && b.status !== 'À VENIR') return -1;
            if (a.status !== 'À VENIR' && b.status === 'À VENIR') return 1;
            if (a.status === 'TERMINÉ' && b.status !== 'TERMINÉ') return 1;
            if (a.status !== 'TERMINÉ' && b.status === 'TERMINÉ') return -1;
            return 0;
          });

          setCompetitions(formattedCompetitions);
        }
        setLoadingCompetitions(false);
      })
      .catch(() => {
        setLoadingCompetitions(false);
      });
  }, []);


  // Compteurs réels, recalculés par l'API à chaque chargement : ajouter une
  // formation ou publier un projet les met à jour sans toucher au code.
  // Ils remplacent quatre valeurs qui étaient écrites en dur (« 180+ élèves »,
  // « 50+ projets », « 100% clients satisfaits ») et ne reposaient sur rien.
  // « Clients satisfaits » a disparu : aucune donnée ne permet de le mesurer.
  const stats = publicStats
    ? [
        { value: `${publicStats.formationStudents}`, label: "Élèves Formés", icon: <GraduationCap className="w-6 h-6" /> },
        { value: `${publicStats.competitionParticipants}`, label: "Participants aux Compétitions", icon: <Trophy className="w-6 h-6" /> },
        { value: `${publicStats.formations}`, label: "Formations", icon: <Star className="w-6 h-6" /> },
        { value: `${publicStats.projects}`, label: "Projets Réalisés", icon: <Rocket className="w-6 h-6" /> },
      ]
    : [];

  const navigation = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/formations', label: 'Formations', icon: GraduationCap },
    { path: '/competitions', label: 'Compétitions', icon: Trophy },
    { path: '/projets', label: 'Projets', icon: Code },
    { path: '/boutique', label: 'Boutique', icon: ShoppingBag }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation Améliorée */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg py-3 shadow-lg shadow-black/50' : 'bg-gradient-to-b from-black/80 to-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
              Phoelynx France
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center lg:space-x-8 md:space-x-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 lg:space-x-2 lg:px-4 md:px-2 py-2 rounded-lg transition-all hover:bg-white/10 ${
                    item.path === '/' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="lg:text-base md:text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* CTA Button Desktop - Temporarily hidden */}
          <div className="hidden">
            <a href="#contact" className="px-4 py-2 bg-gradient-to-r from-red-500 to-sky-500 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-sky-500/30 transition-all">
              DEVIS GRATUIT
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-white/10">
            <div className="flex flex-col space-y-2 pt-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                      item.path === '/' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Gradient Backgrounds */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        {/* Grid Background Effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#ef4444 1px, transparent 1px), linear-gradient(90deg, #38bdf8 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        {/* Floating Tech Icons */}
        <div className="absolute inset-0">
          <Bot className="absolute top-20 left-10 w-20 h-20 text-red-500/30 animate-pulse" />
          <Code className="absolute top-40 right-20 w-16 h-16 text-sky-500/30 animate-pulse delay-100" />
          <Cpu className="absolute bottom-20 left-20 w-24 h-24 text-red-500/30 animate-pulse delay-200" />
          <Rocket className="absolute bottom-40 right-10 w-16 h-16 text-sky-500/30 animate-pulse delay-300" />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-red-500/20 to-sky-500/20 rounded-full mb-4 sm:mb-6 border border-sky-500/30 backdrop-blur-sm">
            <Zap className="w-4 h-4 text-sky-400 animate-pulse" />
            <span className="text-xs sm:text-sm text-sky-300">Innovation & Excellence depuis 2022</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 leading-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-red-500 to-red-600 block">
              {homeHeroTitleLine1}
            </span>
            {homeHeroTitleLine2 && (
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600 block">
                {homeHeroTitleLine2}
              </span>
            )}
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            {homeHeroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/formations" className="group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-red-500/30 transition-all transform hover:scale-105 flex items-center justify-center gap-2 text-sm sm:text-base">
              <span className="hidden sm:inline">DÉCOUVRIR NOS FORMATIONS</span>
              <span className="sm:hidden">NOS FORMATIONS</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/boutique" className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-sky-500/30 transition-all transform hover:scale-105 flex items-center justify-center text-sm sm:text-base">
              <span className="hidden sm:inline">ACCÉDER À LA BOUTIQUE</span>
              <span className="sm:hidden">BOUTIQUE</span>
            </Link>
          </div>
        </div>

        <ChevronDown className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-8 h-8 animate-bounce text-sky-400" />
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
              NOS SERVICES
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">Solutions complètes pour vos projets tech</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {loadingServices ? (
              // Skeleton loader pour les services
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-64 bg-gradient-to-br from-gray-800 to-gray-700 p-[2px] rounded-2xl animate-pulse">
                  <div className="h-full bg-black rounded-2xl p-6">
                    <div className="w-12 h-12 bg-gray-700 rounded-xl mb-4"></div>
                    <div className="h-5 bg-gray-700 rounded mb-2"></div>
                    <div className="h-3 bg-gray-700 rounded w-3/4 mb-3"></div>
                    <div className="h-3 bg-gray-700 rounded mb-2"></div>
                    <div className="h-3 bg-gray-700 rounded w-2/3"></div>
                  </div>
                </div>
              ))
            ) : services.length === 0 ? (
              <div className="col-span-4 text-center py-8">
                <p className="text-gray-400">Aucun service disponible pour le moment.</p>
              </div>
            ) : (
              services.map((service: Service) => (
                <div
                  key={service.id}
                  className="group relative cursor-pointer"
                  onMouseEnter={() => setActiveService(service.id)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  <div className={`h-full bg-gradient-to-br ${service.color} p-[2px] rounded-2xl`}>
                    <div className="h-full bg-black rounded-2xl p-6 hover:bg-gray-900/80 transition-all duration-300">
                      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${service.color} mb-4 shadow-lg group-hover:shadow-2xl transition-all`}>
                        {service.icon}
                      </div>

                      <h3 className="text-xl font-bold mb-1">{service.title}</h3>
                      <p className="text-sm bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent mb-3">{service.subtitle}</p>
                      <p className="text-gray-400 text-sm mb-4">{service.description}</p>

                      <div className={`transition-all duration-300 ${activeService === service.id ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'}`}>
                        <ul className="space-y-1">
                          {service.features.map((feature: string, idx: number) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-sky-400 rounded-full" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Aperçu Formations */}
      <section className="py-20 px-6 bg-gradient-to-b from-black via-gray-900/20 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
              FORMATIONS À LA UNE
            </h2>
            <p className="text-gray-400 text-lg">Développez vos compétences avec nos formations pratiques</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loadingFormations ? (
              // Skeleton loader
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl overflow-hidden border border-gray-800 p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-700 rounded mb-4"></div>
                    <div className="h-6 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded mb-4"></div>
                    <div className="h-8 bg-gray-700 rounded"></div>
                  </div>
                </div>
              ))
            ) : formations && formations.length > 0 ? (
              formations.slice(0, 6).map((formation: any) => (
              <div
                key={formation.id}
                className="group bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl overflow-hidden border border-gray-800 hover:border-sky-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-sky-500/10"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-sm px-3 py-1 bg-gradient-to-r from-red-500/20 to-sky-500/20 text-sky-300 rounded-full flex items-center gap-1 border border-sky-500/30">
                      <Calendar className="w-3 h-3" />
                      {formation.date}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      formation.level === 'Débutant' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      formation.level === 'Intermédiaire' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      formation.level === 'Avancé' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    }`}>
                      {formation.level}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold mb-2 group-hover:text-sky-400 transition-colors">
                    {formation.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{formation.description}</p>

                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">{formation.price}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formation.duration}
                    </span>
                  </div>
                </div>
              </div>
              ))
            ) : (
              // Message quand pas de formations à venir
              <div className="col-span-3 text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Aucune formation programmée prochainement</h3>
                <p className="text-gray-500">Consultez toutes nos formations ou contactez-nous pour plus d'informations</p>
              </div>
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/formations" className="inline-flex px-8 py-3 bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-sky-500/25 transition-all">
              VOIR TOUTES LES FORMATIONS →
            </Link>
          </div>
        </div>
      </section>

      {/* Competitions Section */}
      <section id="competitions" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
              ÉVÉNEMENTS ET COMPÉTITIONS ROBOTIQUE
            </h2>
            <p className="text-gray-400 text-lg mb-6">Accompagnement d'équipes du niveau académique à l'international</p>
            <Link
              to="/competitions"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-orange-500/30 transition-all hover:scale-105"
            >
              <Trophy className="h-5 w-5" />
              <span>Voir toutes les compétitions</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>

          <div className="space-y-4">
            {loadingCompetitions ? (
              // Skeleton loader pour compétitions
              Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="border border-gray-800 rounded-xl p-6">
                  <div className="animate-pulse">
                    <div className="flex items-center gap-6">
                      <div className="w-24 h-16 bg-gray-700 rounded"></div>
                      <div className="flex-1">
                        <div className="h-6 bg-gray-700 rounded mb-2"></div>
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                      </div>
                      <div className="w-24 h-8 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : competitions.length > 0 ? (
              competitions.map((comp, index) => (
              <div key={index} className={`group border rounded-xl p-6 transition-all duration-300 ${
                comp.status === 'TERMINÉ'
                  ? 'border-gray-700 bg-gray-900/20 hover:border-gray-600'
                  : 'border-gray-800 hover:border-sky-500/50 hover:bg-gradient-to-r hover:from-red-900/5 hover:to-sky-900/5'
              }`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${
                        comp.status === 'TERMINÉ'
                          ? 'text-gray-500'
                          : 'bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent'
                      }`}>
                        {comp.date}
                      </p>
                    </div>
                    <div>
                      <h3 className={`text-2xl font-bold transition-all ${
                        comp.status === 'TERMINÉ'
                          ? 'text-gray-400'
                          : 'group-hover:bg-gradient-to-r group-hover:from-red-400 group-hover:to-sky-400 group-hover:bg-clip-text group-hover:text-transparent'
                      }`}>
                        {comp.event}
                      </h3>
                      <p className={`flex items-center gap-2 mt-1 ${
                        comp.status === 'TERMINÉ' ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        <MapPin className="w-4 h-4" />
                        {comp.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                      comp.status === 'TERMINÉ' ? 'bg-gray-700/50 text-gray-400 border border-gray-600' :
                      comp.status === 'À VENIR' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      comp.status === 'QUALIFICATION REQUISE' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                      comp.status === 'PLACES LIMITÉES' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      comp.status === 'PRÉPARATION' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                      'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}>
                      {comp.status}
                    </span>
                    <button className="px-6 py-2 bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-sky-500/30 transition-all group-hover:scale-105">
                      {comp.status === 'TERMINÉ' ? 'RÉSULTATS' : 'INFOS'}
                    </button>
                  </div>
                </div>
              </div>
              ))
            ) : (
              // Message quand pas de compétitions
              <div className="text-center py-12">
                <Trophy className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Aucune compétition planifiée</h3>
                <p className="text-gray-500">Les prochaines compétitions seront bientôt annoncées</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats Section — masquée tant que l'API n'a pas renvoyé les compteurs,
          pour éviter d'afficher une rangée de zéros pendant le chargement. */}
      {stats.length > 0 && (
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900/30 via-black to-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex p-2 sm:p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20 mb-3 sm:mb-4 group-hover:scale-110 transition-transform border border-sky-500/30">
                  <div className="w-5 h-5 sm:w-6 sm:h-6">
                    {stat.icon}
                  </div>
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
                  {stat.value}
                </h3>
                <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Contact Section */}
      {/* Témoignages — masqué tant qu'aucun avis n'est publié (aucun faux avis). */}
      {testimonials.length > 0 && (
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 sm:mb-12 text-center bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
              ILS NOUS FONT CONFIANCE
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-xl border border-gray-800 bg-gray-900/40 flex flex-col">
                  {typeof t.rating === 'number' && (
                    <div className="flex mb-3" aria-label={`${t.rating} sur 5`}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}`} />
                      ))}
                    </div>
                  )}
                  <p className="text-gray-300 text-sm sm:text-base italic flex-1">« {t.content} »</p>
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <p className="font-semibold text-white text-sm">{t.name}</p>
                    {(t.position || t.company) && (
                      <p className="text-gray-400 text-xs">
                        {[t.position, t.company].filter(Boolean).join(' — ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="contact" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
            CONTACTEZ-NOUS
          </h2>
          <p className="text-gray-400 text-base sm:text-lg mb-8 sm:mb-12">Discutons de votre projet tech ou de formation</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="group p-4 rounded-xl border border-gray-800 hover:border-sky-500/50 transition-all duration-300">
              <div className="inline-flex p-3 sm:p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20 mb-4 border border-sky-500/30 group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-sky-400" />
              </div>
              <h3 className="font-bold mb-2 text-base sm:text-lg">Téléphone</h3>
              <p className="text-gray-400 text-sm sm:text-base">À venir</p>
            </div>

            <div className="group p-4 rounded-xl border border-gray-800 hover:border-sky-500/50 transition-all duration-300">
              <div className="inline-flex p-3 sm:p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20 mb-4 border border-sky-500/30 group-hover:scale-110 transition-transform">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-sky-400" />
              </div>
              <h3 className="font-bold mb-2 text-base sm:text-lg">Email</h3>
              <p className="text-gray-400 text-sm sm:text-base">{contactEmail}</p>
            </div>

            <div className="group p-4 rounded-xl border border-gray-800 hover:border-sky-500/50 transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="inline-flex p-3 sm:p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20 mb-4 border border-sky-500/30 group-hover:scale-110 transition-transform">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-sky-400" />
              </div>
              <h3 className="font-bold mb-2 text-base sm:text-lg">Zones d'intervention</h3>
              <p className="text-gray-400 text-sm sm:text-base">Bordeaux Métropole :</p>
              <p className="text-gray-400 text-xs sm:text-sm">Bordeaux • Gradignan • Talence • Pessac</p>
              <p className="text-gray-400 text-xs sm:text-sm">Gironde Sud : Bazas</p>
              <p className="text-gray-400 text-xs mt-2">Siège : Saint-Nexans, Dordogne</p>
            </div>
          </div>

          {contactStatus === 'sent' ? (
            <div className="max-w-xl mx-auto p-6 rounded-xl border border-sky-500/40 bg-sky-500/10">
              <p className="text-lg font-semibold text-sky-300">Merci, votre message a bien été envoyé.</p>
              <p className="text-gray-400 text-sm mt-2">Nous vous répondrons dans les meilleurs délais. Un accusé de réception vient de vous être adressé.</p>
              <button
                onClick={() => setContactStatus('idle')}
                className="mt-4 text-sm text-sky-400 hover:text-sky-300 underline"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="max-w-xl mx-auto text-left space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text" required placeholder="Prénom" aria-label="Prénom"
                  value={contactForm.firstName}
                  onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
                />
                <input
                  type="text" required placeholder="Nom" aria-label="Nom"
                  value={contactForm.lastName}
                  onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="email" required placeholder="Email" aria-label="Email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
                />
                <input
                  type="tel" placeholder="Téléphone (facultatif)" aria-label="Téléphone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
                />
              </div>
              <input
                type="text" required placeholder="Sujet" aria-label="Sujet"
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
              />
              <textarea
                required placeholder="Votre message" aria-label="Message" rows={5}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none resize-y"
              />
              {contactStatus === 'error' && (
                <p className="text-red-400 text-sm">{contactError}</p>
              )}
              <button
                type="submit"
                disabled={contactStatus === 'sending'}
                className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-sky-500/30 transition-all transform hover:scale-105 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {contactStatus === 'sending' ? 'ENVOI EN COURS…' : 'ENVOYER LE MESSAGE'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          {/* Inscription newsletter */}
          <div className="mb-8 sm:mb-10 pb-8 sm:pb-10 border-b border-gray-800 text-center">
            <h3 className="text-lg sm:text-xl font-bold mb-2">Restez informé</h3>
            <p className="text-gray-400 text-sm mb-4">Formations, compétitions et actualités, directement dans votre boîte mail.</p>
            {newsletterStatus === 'sent' ? (
              <p className="text-sky-300 font-medium">{newsletterMessage}</p>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email" required placeholder="Votre adresse email" aria-label="Adresse email newsletter"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === 'sending'}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-sky-500/30 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {newsletterStatus === 'sending' ? '…' : "S'inscrire"}
                </button>
              </form>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-red-400 text-sm mt-3">{newsletterMessage}</p>
            )}
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-center gap-6 sm:gap-8">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </div>

            <div className="flex items-center gap-4 sm:gap-6 order-2 lg:order-none">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-sky-400 cursor-pointer transition-colors" />
              <span className="text-gray-400 text-sm sm:text-base">phoelynx.fr</span>
            </div>

            <div className="flex flex-col items-center lg:items-end gap-2 order-3 lg:order-none">
              <p className="text-gray-400 text-xs sm:text-sm text-center lg:text-right">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex flex-wrap justify-center lg:justify-end gap-3 sm:gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-sky-400 transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-sky-400 transition-colors">
                  Confidentialité
                </Link>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-sky-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PhoelynxPlatform;